import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Icon from '../../components/icon/Icon';
import Badge from '../../components/badge/Badge';
import Table from '../../components/table/table';
import TableSkeleton from '../../components/TableSkeleton/TableSkeleton';
import { getAllApplicationsByCuit } from '../../services/ApplicationService';
import { ITableData, ApplicationListData } from '../../types/interfaces';
import { hasData, isSuccessfulResponse } from '../../utils/Utils';
import { routes } from '../../routes/routePaths';
import {
  buildListState,
  DASHBOARD_KEYS,
  type ListNavigationState,
} from '../../utils/listNavigationState';
import { APPLICATION_STATUS } from '../../utils/Constants';
import useRole from '../../hooks/useRole';
import { getStatusText } from '../../utils/applicationStatusUtils';
import { useConfig } from '../../context/ConfigContext';
import { resolveLocalCurrencyCode } from '../../utils/quotationAssets';

interface SolicitudesTabProps {
  cuit: string;
  listState?: ListNavigationState;
}

const SolicitudesTab: React.FC<SolicitudesTabProps> = ({ cuit, listState }) => {
  const navigate = useNavigate();
  const { isCommercial, isComite, isInstrumentator } = useRole();
  const localCurrencyCode = resolveLocalCurrencyCode(useConfig());
  const [applications, setApplications] = useState<ApplicationListData[]>([]);
  const [tableData, setTableData] = useState<ITableData>({ 
    page: 0, 
    size: 50, 
    totalElements: 0, 
    totalPages: 0 
  });
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Funciones helper
  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('es-AR');
    } catch {
      return 'N/A';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusVariant = (statusCode: string) => {
    const statusMap: { [key: string]: string } = {
      'PEN': 'warning',
      'APP': 'success',
      'REJ': 'danger',
      'INS': 'info',
      'COM': 'success',
    };
    return statusMap[statusCode] || 'neutral';
  };

  const cleanStatusLabel = (statusLabel: string) => {
    if (!statusLabel) return 'Sin estado';
    if (statusLabel.toLowerCase().includes('documentación en revision')) {
      return 'En revision';
    }
    return statusLabel;
  };

  // Definición de columnas
  const columns = [
    {
      name: 'Usuario',
      selector: (row: ApplicationListData) => row.producer ? `${row.producer.first_name} ${row.producer.last_name}` : 'N/A',
      cell: (row: ApplicationListData) => (
        <P type="2" variant="neutral" modifier="regular" withMargin={false}>
          {row.producer ? `${row.producer.first_name} ${row.producer.last_name}` : 'N/A'}
        </P>
      ),
      sortable: true,
    },
    {
      name: 'Comercial',
      selector: (row: ApplicationListData) => row.commercial ? `${row.commercial.first_name} ${row.commercial.last_name}` : 'N/A',
      cell: (row: ApplicationListData) => (
        <P type="2" variant="neutral" modifier="regular" withMargin={false}>
          {row.commercial ? `${row.commercial.first_name} ${row.commercial.last_name}` : 'N/A'}
        </P>
      ),
      sortable: true,
    },
    {
      name: 'Referido/Vendedor',
      selector: (row: ApplicationListData) => {
        const referido = row.referido;
        return !referido || referido === '-' ? 'Generación propia' : referido;
      },
      cell: (row: ApplicationListData) => {
        const referido = row.referido;
        const displayValue = !referido || referido === '-' ? 'Generación propia' : referido;
        return (
          <P type="2" variant="neutral" modifier="regular" withMargin={false}>
            {displayValue}
          </P>
        );
      },
      sortable: false,
    },
    {
      name: 'Modificado',
      selector: (row: ApplicationListData) => row.updated_date,
      cell: (row: ApplicationListData) => (
        <P type="2" variant="neutral" modifier="regular" withMargin={false}>
          {formatDate(row.updated_date)}
        </P>
      ),
      sortable: true,
    },
    {
      name: 'Monto',
      selector: (row: ApplicationListData) => row.total_ars || 0,
      cell: (row: ApplicationListData) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <P type="2" variant="neutral" modifier="regular" withMargin={false}>
            <span style={{ fontWeight: 'bold' }}>{localCurrencyCode}</span> ${formatCurrency(row.total_ars || 0)}
          </P>
          <P type="2" variant="neutral" modifier="weak" withMargin={false}>
            <span style={{ fontWeight: 'bold' }}>USD</span> ${formatCurrency(row.total_usd || 0)}
          </P>
        </div>
      ),
      sortable: true,
    },
    {
      name: 'Solicitud',
      selector: (row: ApplicationListData) => row.id,
      cell: (row: ApplicationListData) => (
        <div
          style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${routes.clients}/${row.cuit}/application/${row.id}`, {
              state: {
                status: row.status?.id || 0,
                statusCode: row.status?.code,
                fromSolicitudesTab: true,
                isReadOnly: true,
                listState:
                  listState ??
                  buildListState({
                    dashboardKey: DASHBOARD_KEYS.clients,
                    page: tableData.page,
                    searchText: '',
                    activeFilters: {},
                  }),
              },
            });
          }}
          role="button"
          aria-label="Ver solicitud"
        >
          <Icon 
            name="view_outline" 
            variant="info" 
            modifier="strong"
          />
        </div>
      ),
      center: true,
      width: '100px',
    },
    {
      name: 'Estado',
      selector: (row: ApplicationListData) =>
        row.status?.code || row.status?.id
          ? getStatusText(row.status.code ?? row.status.id)
          : 'Sin estado',
      cell: (row: ApplicationListData) => {
        const statusText =
          row.status?.code || row.status?.id
            ? getStatusText(row.status.code ?? row.status.id)
            : 'Sin estado';
        return (
          <Badge 
            type="text" 
            variant={getStatusVariant(row.status?.code || '')} 
            size="sm"
          >
            {statusText}
          </Badge>
        );
      },
      sortable: true,
    },
  ];

  useEffect(() => {
    fetchApplications();
  }, [cuit]);

  const fetchApplications = async () => {
    if (!cuit) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getAllApplicationsByCuit(cuit);
      
      if (hasData(response) && isSuccessfulResponse(response)) {
        const applicationsData = response.data.content || [];
        // Usar directamente los datos que vienen del backend
        setApplications(applicationsData);
        setTableData({
          page: response.data.page || 0,
          size: response.data.size || 50,
          totalElements: response.data.totalElements || 0,
          totalPages: response.data.totalPages || 0,
        });
      } else {
        setApplications([]);
        setTableData({ page: 0, size: 50, totalElements: 0, totalPages: 0 });
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Error al cargar las solicitudes');
      setApplications([]);
      setTableData({ page: 0, size: 50, totalElements: 0, totalPages: 0 });
    } finally {
      setIsLoading(false);
    }
  };



  if (error) {
    return (
      <Card spacing="4" display="flex" justifyContent="center" alignItems="center" height="200px">
        <H type="2" variant="danger" modifier="regular" withMargin={false}>
          {error}
        </H>
      </Card>
    );
  }

  return (
    <Card spacing="4">
      <Card margin={{ bottom: '4' }}>
        <H type="2" variant="neutral" weight="bold" modifier="strong" withMargin={false}>
          Solicitudes
        </H>
      </Card>

      {isLoading ? (
        <Card margin={{ top: '4' }}>
          <TableSkeleton rows={8} columns={7} />
        </Card>
      ) : (
        <Card margin={{ top: '2' }}>
          {!applications || applications.length === 0 ? (
            <Card display="flex" justifyContent="center" alignItems="center" height="200px">
              <P type="1" variant="neutral" modifier="weak" withMargin={false}>
                No se encontraron solicitudes para este cliente
              </P>
            </Card>
          ) : (
            <Table
              columns={columns}
              data={applications}
              tableData={tableData}
            />
          )}
        </Card>
      )}
    </Card>
  );
};

export default SolicitudesTab;